import { Link, Outlet } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { NavMenu } from '@/components/NavMenu'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/useAuth'
import { useMe } from '@/lib/auth/useMe'

/**
 * Uygulama kabuğu: gezinme + kullanıcı şeridi.
 *
 * Oturum yokken hiçbir şey çizilmez, yalnız içerik: giriş ve şifre ekranları kabuğun içinde ama
 * menüsüz görünür — girmemiş birine gezinemeyeceği bağlantılar göstermek onu 403'e davet etmektir.
 *
 * Bakiye rozeti (`docs/spec/07-pages-ui.md §Genel UX`) burada yok: kredi Faz 3 ile geliyor ve
 * hep "0 ₺" yazan bir rozet, çalışan bir göstergeye benzerdi.
 */
export function AppShell() {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()

  // Firma adı token'da yok; oturumu olan kullanıcı için tek kaynağı bu uç (PR #40).
  const { data: profile } = useMe()

  const isSuperAdmin = user?.role === 'SuperAdmin'

  return (
    <div className="bg-background flex min-h-dvh flex-col">
      {/*
        İçeriğe atlama bağlantısı (WCAG 2.4.1). Süper adminde gezinme **on bir** bağlantı: onsuz
        klavye kullanan biri her sayfada, her seferinde o on biri geçmek zorunda kalıyor.
        Landmark'lar (banner/navigation/main) zaten var ama onlar yalnız ekran okuyucuya yol
        açıyor; klavyeyle gezen gören kullanıcının landmark'ı yok.

        Yalnız odaklanınca görünür: `sr-only`, `focus:not-sr-only` ile geri alınıyor. Görünmez ama
        odaklanabilir bir bağlantı bırakmak, turu "boşluğa odaklanan" bir durakla başlatırdı.
      */}
      <a
        href="#main-content"
        className="bg-background text-foreground focus:ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:border focus:px-3 focus:py-2 focus:text-sm focus:ring-2 focus:outline-none"
      >
        {t('nav.skipToContent')}
      </a>

      <header className="border-border border-b">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-6">
          <span className="text-primary text-base font-semibold tracking-tight">
            {t('app.name')}
          </span>

          {user === null ? null : (
            <>
              <nav aria-label={t('nav.label')} className="flex items-center gap-1">
                {isSuperAdmin ? (
                  <>
                    {/* Panonun adresi diğerlerinin öneki; tam eşleşmeyle işaretlenir, yoksa
                        yönetim tarafındaki her sayfada "Pano" da aktif görünür. */}
                    <NavLink to="/admin" exact>
                      {t('nav.adminHome')}
                    </NavLink>
                    {/* Şeritte duran dördü, operatörün her gün açtıkları (KARAR 2026-08-20,
                        kullanıcı). Gerisi tek menüde: on bir bağlantı şeridi iki satıra
                        düşürüyordu ve iki satırlık bir gezinme, hiyerarşisi olmayan bir
                        listedir. */}
                    <NavLink to="/admin/tenants">{t('nav.tenants')}</NavLink>
                    <NavLink to="/admin/queries">{t('nav.adminQueries')}</NavLink>
                    <NavLink to="/admin/requests">{t('nav.requests')}</NavLink>

                    <NavMenu
                      label={t('nav.manage')}
                      sections={[
                        {
                          title: t('nav.groups.monitoring'),
                          items: [
                            { to: '/admin/companies', label: t('nav.adminCompanies') },
                            { to: '/admin/mersis', label: t('nav.adminMersis') },
                            { to: '/admin/audit-logs', label: t('nav.audit') },
                          ],
                        },
                        {
                          title: t('nav.groups.platform'),
                          items: [
                            { to: '/admin/pricing', label: t('nav.pricing') },
                            { to: '/admin/settings', label: t('nav.settings') },
                            { to: '/admin/rate-limits', label: t('nav.rateLimits') },
                            { to: '/admin/super-admins', label: t('nav.superAdmins') },
                          ],
                        },
                      ]}
                    />
                  </>
                ) : (
                  <>
                    {/* Panonun ve geçmişin adresi başkalarının önekidir; ikisi tam eşleşmeyle
                        işaretlenir, yoksa "Yeni Sorgu" açıkken üç bağlantı birden aktif görünür. */}
                    <NavLink to="/portal" exact>
                      {t('nav.portal')}
                    </NavLink>
                    <NavLink to="/portal/queries/new">{t('nav.newQuery')}</NavLink>
                    <NavLink to="/portal/queries" exact>
                      {t('nav.queryHistory')}
                    </NavLink>
                    <NavLink to="/portal/batches" exact>
                      {t('nav.batches')}
                    </NavLink>

                    {/* Şeritte duran dördü sorgunun kendi döngüsü: pano → yeni sorgu → geçmiş →
                        toplu. Gerisi menüde.

                        **P6 nihayet gezinmeye girdi:** "Kredi & Kullanım" bugüne kadar yalnız
                        panodaki bağlantıdan açılıyordu ve P1–P9 içinde menüde yeri olmayan tek
                        ekrandı.

                        Ekip bölümü yalnız firma yöneticisinde; sıradan kullanıcıya girebileceği
                        bir bağlantı gösterilmiyor (route guard'ı da reddediyor). Şerit onun
                        rolünde zaten tek satıra sığıyordu, ama menü ikisinde de aynı şekilde
                        duruyor: gezinmenin biçimi role göre değişseydi, meslektaşına "soldan
                        üçüncü" diyen kullanıcı yanlış yeri tarif ederdi. */}
                    <NavMenu
                      label={t('nav.account')}
                      sections={[
                        {
                          title: t('nav.groups.usage'),
                          items: [
                            { to: '/portal/credits', label: t('nav.credits') },
                            { to: '/portal/companies', label: t('nav.companies') },
                          ],
                        },
                        ...(user.role === 'TenantAdmin'
                          ? [
                              {
                                title: t('nav.groups.team'),
                                items: [
                                  { to: '/portal/users', label: t('nav.users') },
                                  { to: '/portal/api-clients', label: t('nav.apiClients') },
                                ],
                              },
                            ]
                          : []),
                      ]}
                    />
                  </>
                )}
              </nav>

              <div className="ml-auto flex items-center gap-3">
                {/* Hangi firmanın içinde olduğunu göstermek çok kiracılı bir üründe kimlik
                    kadar önemli; süper adminin firması yoktur, satır da çizilmez. */}
                {profile?.tenantName == null ? null : (
                  <span className="text-muted-foreground hidden text-sm sm:inline">
                    {profile.tenantName}
                  </span>
                )}

                <Link
                  to="/profile"
                  className="text-foreground focus-visible:ring-ring rounded-md px-2 py-1 text-sm font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
                >
                  {user.fullName}
                </Link>

                <ThemeToggle />

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void signOut()
                  }}
                >
                  {t('nav.signOut')}
                </Button>
              </div>
            </>
          )}
        </div>
      </header>

      {/*
        `tabIndex={-1}`: atlama bağlantısı odağı buraya **taşıyabilsin** diye. Yalnız `id` verilirse
        tarayıcı sayfayı kaydırır ama odak bağlantıda kalır ve bir sonraki Tab yine gezinmeye döner
        — yani atlama görsel olarak çalışır, klavye için hiçbir şey yapmaz.
      */}
      <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-7xl flex-1 px-6 py-8">
        <Outlet />
      </main>
    </div>
  )
}

interface NavLinkProps {
  readonly to: string
  readonly children: string
  /** Yalnız bu adres açıkken işaretlensin; alt adresler bağlantıyı aktif yapmasın. */
  readonly exact?: boolean
}

/** Aktif bağlantı yalnız renkle değil `aria-current` ile de bildirilir (standards §4). */
function NavLink({ to, children, exact = false }: NavLinkProps) {
  return (
    <Link
      to={to}
      activeOptions={{ exact }}
      className="text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:ring-ring aria-[current=page]:text-primary aria-[current=page]:bg-accent rounded-md px-3 py-2 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
      activeProps={{ 'aria-current': 'page' }}
    >
      {children}
    </Link>
  )
}
